import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, Button, Input, Textarea } from '../components/common';
import { emailsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { useMediaQuery } from '../hooks';

const TEMPLATES = [
  { label: 'Follow Up',     subject: 'Following up on our conversation',      body: '<p>Hi {{name}},</p><p>I wanted to follow up on our recent conversation. Please let me know if you have any questions.</p><p>Best,<br/>{{sender}}</p>' },
  { label: 'Introduction',  subject: 'Nice to meet you!',                      body: '<p>Hi {{name}},</p><p>My name is {{sender}} and I wanted to introduce myself. Looking forward to connecting with you.</p>' },
  { label: 'Proposal',      subject: 'Proposal for {{name}}',                  body: '<p>Dear {{name}},</p><p>Please find attached our proposal. We believe this will be a great fit for your needs.</p><p>Let me know your thoughts.</p>' },
];

export const ComposePage = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [form, setForm] = useState({ to: '', toName: '', subject: '', htmlBody: '', tags: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const applyTemplate = (t) => setForm((f) => ({ ...f, subject: t.subject, htmlBody: t.body }));

  const validate = () => {
    const e = {};
    if (!form.to || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.to)) e.to = 'Valid email required';
    if (!form.subject.trim()) e.subject = 'Subject required';
    if (!form.htmlBody.trim()) e.htmlBody = 'Body required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSend = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const tags = form.tags.split(',').map((t) => t.trim()).filter(Boolean);
      await emailsAPI.send({ to: form.to, toName: form.toName || undefined, subject: form.subject, htmlBody: form.htmlBody, tags });
      toast.success('Email sent with tracking!');
      navigate('/emails');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout>
      <PageHeader
        title="Compose Email"
        subtitle="Send a tracked email — pixel and link tracking injected automatically"
      />
      <div style={{ padding: isMobile ? '16px' : '24px 32px', maxWidth: 800, display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Templates */}
        <Card style={{ padding: '16px 20px' }}>
          <p style={{ fontSize: '0.68rem', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>Quick Templates</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {TEMPLATES.map((t) => (
              <button key={t.label} onClick={() => applyTemplate(t)} style={{
                padding: '5px 12px', borderRadius: 'var(--radius-sm)',
                fontSize: '0.75rem', fontFamily: 'var(--font-mono)',
                background: 'var(--bg-elevated)', color: 'var(--text-secondary)',
                border: '1px solid var(--border)', cursor: 'pointer', transition: 'all 120ms',
              }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--cyan)'; e.currentTarget.style.color = 'var(--cyan)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}>
                {t.label}
              </button>
            ))}
          </div>
        </Card>

        {/* Form */}
        <Card style={{ padding: isMobile ? '16px' : '24px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
              <Input label="To (Email) *" type="email" value={form.to} onChange={set('to')} error={errors.to} placeholder="recipient@example.com" />
              <Input label="To (Name)" value={form.toName} onChange={set('toName')} placeholder="Alice Smith" />
            </div>
            <Input label="Subject *" value={form.subject} onChange={set('subject')} error={errors.subject} placeholder="Your email subject…" />
            <Input label="Tags (comma-separated)" value={form.tags} onChange={set('tags')} placeholder="followup, q4, proposal" />

            {/* Body */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase' }}>HTML Body *</label>
                <button onClick={() => setPreview((p) => !p)} style={{
                  fontSize: '0.7rem', fontFamily: 'var(--font-mono)', color: 'var(--cyan)',
                  background: 'none', border: 'none', cursor: 'pointer',
                }}>{preview ? '< source' : '◉ preview'}</button>
              </div>
              {preview ? (
                <div style={{
                  padding: 16, background: '#fff', borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-light)', minHeight: 200,
                  color: '#333', fontSize: '0.9rem',
                }} dangerouslySetInnerHTML={{ __html: form.htmlBody }} />
              ) : (
                <Textarea
                  value={form.htmlBody}
                  onChange={set('htmlBody')}
                  error={errors.htmlBody}
                  placeholder="<p>Hello,</p><p>Your email content here. Links will be automatically tracked.</p>"
                  style={{ minHeight: 200, fontFamily: 'var(--font-mono)', fontSize: '0.82rem' }}
                />
              )}
            </div>

            {/* Tracking info banner */}
            <div style={{
              padding: '12px 16px', borderRadius: 'var(--radius-md)',
              background: 'var(--cyan-dim)', border: '1px solid rgba(0,225,200,0.2)',
              fontSize: '0.78rem', color: 'var(--cyan)', fontFamily: 'var(--font-mono)',
            }}>
              ✓ A 1×1 tracking pixel will be injected automatically.&nbsp;
              ✓ All links (&lt;a href&gt;) will be rewritten to tracked redirect URLs.
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 4, flexDirection: isMobile ? 'column-reverse' : 'row' }}>
              <Button variant="secondary" onClick={() => navigate('/emails')}>Cancel</Button>
              <Button loading={loading} onClick={handleSend} style={{ minWidth: 120, fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }}>
                Send Email ✉
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </AppLayout>
  );
};
